import AccountNav from "../../account/AccountNav";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Dialog, Typography, Button as MuiButton } from "@mui/material";
import { useHistory, Link } from "react-router-dom";
import {
  Ticket,
  Dices,
  Pickaxe,
  Rocket,
  ChevronDown,
  Menu,
  X,
  User,
} from "lucide-react";
import { gsap } from "gsap";
import "./navbar.css";

import { Path, LOTTERY_NAMES } from "../../../../static/Constants";
import { networkID } from "../../../../static/utils";
import {
  getConnectedAccount,
  getSavedAccount,
  connectWallet,
  checkNetwork,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";
import { colors } from "../../../../theme/colors";
import { typography } from "../../../../theme/typography";

import {
  CommonDropdown,
  CommonDropdownItem,
} from "../../CommonDropdown/CommonDropdown";
import CommonButton from "../../ui/inputs/CommonButton";
import { toast } from "../../ui/feedback/CommonToast";

// Extracted Animations
import {
  animateNavHover,
  animateNavLeave,
  initNavbarScrollAnimation,
  animateMobileNavBgIn,
  animateMobileNavBgOut,
  initMobileMenuAnimation,
} from "../animations/navbarAnimations";

const NavBar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState(null);
  const [walletAccount, setWalletAccount] = useState(() => getSavedAccount());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const navbarRef = useRef(null);
  const logoImageRef = useRef(null);
  const logoTextRef = useRef(null);
  const mobileMenuTl = useRef(null);
  const underlineRef = useRef(null);

  const history = useHistory();

  // =====================================================
  // METAMASK / WEB3 (REACTIVE & PERSISTENT)
  // =====================================================
  const getuser = useCallback(async () => {
    const account = await getConnectedAccount();
    setWalletAccount(account);
  }, []);

  useEffect(() => {
    getuser();
    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => setWalletAccount(acc),
      onChainChanged: () => getuser(),
      onDisconnect: () => setWalletAccount(""),
    });
    return unsubscribe;
  }, [getuser]);

  const handleConnectWallet = async () => {
    const res = await connectWallet();
    if (res.account) {
      setWalletAccount(res.account);

      const netCheck = await checkNetwork(networkID);
      if (!netCheck.isCorrect) {
        setIsModalOpen(true);
        setModalMessage(`Please Connect to Main net (Network Id ${networkID})`);
      }
    } else if (res.error === "NO_WALLET") {
      setIsModalOpen(true);
      setModalMessage(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  // =====================================================
  // UI HANDLERS & GSAP ANIMATIONS
  // =====================================================

  // Sticky Navbar Scroll
  useEffect(() => {
    return initNavbarScrollAnimation(navbarRef, logoImageRef, logoTextRef);
  }, []);

  const handleNavHover = (e) => animateNavHover(underlineRef, e.currentTarget);
  const handleNavLeave = () => animateNavLeave(underlineRef);

  // Lock body scroll on mobile menu
  useEffect(() => {
    const handleScrollLock = () => {
      if (isMobileMenuOpen && window.innerWidth <= 1050) {
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        if (scrollbarWidth > 0)
          document.body.style.paddingRight = `${scrollbarWidth}px`;
      } else {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };

    handleScrollLock();
    window.addEventListener("resize", handleScrollLock);
    return () => {
      window.removeEventListener("resize", handleScrollLock);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isMobileMenuOpen]);

  // Handle Resize Breakpoints & Timeline Recreation
  useEffect(() => {
    let ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);

    const handleResize = () => {
      if (window.innerWidth > 1050) {
        setIsMobileMenuOpen(false);
        setExpandedMobileDropdown(null);
      }

      const wasMobile = mobileMenuTl.current !== null;
      const isMobile = window.innerWidth <= 1050;

      // Only rebuild GSAP context when crossing the breakpoint
      if (wasMobile !== isMobile) {
        if (!isMobile) {
          gsap.set(".navbarcollapse", { clearProps: "opacity,visibility" });
          gsap.set(".mobile-nav-item", { clearProps: "opacity,transform" });
        }
        ctx?.revert();
        ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);
      }
    };

    window.addEventListener("resize", handleResize);
    const menuTimeline = mobileMenuTl.current;
    return () => {
      window.removeEventListener("resize", handleResize);
      menuTimeline?.kill();
      ctx?.revert();
    };
  }, []);

  // Trigger Mobile Menu Timeline
  useEffect(() => {
    if (window.innerWidth <= 1050 && mobileMenuTl.current) {
      if (isMobileMenuOpen) {
        mobileMenuTl.current.timeScale(1).play();
      } else {
        mobileMenuTl.current.timeScale(1).reverse();
      }
    }
  }, [isMobileMenuOpen]);

  return (
    <Box
      component="nav"
      ref={navbarRef}
      className={`navbar autobet-navbar fixed-top ${
        isMobileMenuOpen ? "expanded" : ""
      }`}
      sx={{ flexWrap: "nowrap" }}
      style={{
        "--nav-color": colors.glassNav.text,
        "--nav-hover-color": colors.glassNav.hover,
        "--nav-bg": colors.glassNav.bg,
        "--nav-border": colors.glassNav.border,
        "--nav-font-family": typography.fontFamily,
        "--nav-font-size": "0.875rem",
        "--nav-font-weight": 600,
      }}
    >
      <Box className="navbar-nav mr-auto">
        <Box
          component={Link}
          to={Path.root}
          className="nav-link Logo ml-sm-3 ml-0"
        >
          <Box
            component="img"
            alt="AutoBET"
            ref={logoImageRef}
            onClick={(e) => {
              e.preventDefault();
              history.push("/");
            }}
            src={require("../../../../static/Images/logo.png")}
            style={{
              width: 32,
              height: 32,
              cursor: "pointer",
              marginRight: "6px",
            }}
          />
          <Box component="span" className="autobet-logo-text">
            <Box component="span" className="autobet-logo-auto">
              Auto
            </Box>
            <Box component="span" className="autobet-logo-bet">
              BET
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="button"
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMobileMenuOpen}
        aria-controls="basic-navbar-nav"
        className="navbar-toggler custom-toggler border-0 shadow-none p-0 ml-auto"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? (
          <X size={28} color={colors.glassNav.text} />
        ) : (
          <Menu size={28} color={colors.glassNav.text} />
        )}
      </Box>

      <Box
        id="basic-navbar-nav"
        className={`navbar-collapse collapse justify-content-end navbarcollapse ${
          isMobileMenuOpen ? "show" : ""
        }`}
      >
        <Box
          className="navbar-nav"
          style={{
            position: "relative",
            alignItems: "center",
            gap: "1.25rem",
            flexDirection: "row",
          }}
          onMouseLeave={() => {
            handleNavLeave();
          }}
        >
          <Box
            ref={underlineRef}
            style={{
              position: "absolute",
              bottom: "0px",
              height: "2px",
              backgroundColor: colors.primary.light,
              opacity: 0,
              pointerEvents: "none",
              zIndex: 10,
              left: 0,
              width: 0,
              borderRadius: "2px",
            }}
          />

          {/* Explosive Lotteries Dropdown */}
          <Box
            component="div"
            className={`nav-link hovernav mobile-nav-item mobile-dropdown-trigger ${
              expandedMobileDropdown === "lotteries" ? "expanded" : ""
            }`}
            onMouseEnter={(e) => {
              if (window.innerWidth > 1050) {
                handleNavLeave();
                setIsDropdownOpen(true);
              } else {
                animateMobileNavBgIn(e);
              }
            }}
            onTouchStart={animateMobileNavBgIn}
            onMouseLeave={(e) => {
              if (window.innerWidth > 1050) {
                setIsDropdownOpen(false);
              } else {
                animateMobileNavBgOut(e);
              }
            }}
            onTouchEnd={animateMobileNavBgOut}
            onClick={(e) => {
              if (
                e.target.closest &&
                e.target.closest(".mobile-lottery-submenu")
              )
                return;
              e.preventDefault();
              e.stopPropagation();
              if (window.innerWidth <= 1050)
                setExpandedMobileDropdown((prev) =>
                  prev === "lotteries" ? null : "lotteries"
                );
            }}
            style={{ position: "relative" }}
          >
            <Box
              className="mobile-hover-bg mobile-only"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "0%",
                opacity: 0,
                backgroundColor: colors.primary.light,
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <Box
              className="nav-link-content d-flex justify-content-center align-items-center gap-2"
              style={{ position: "relative", zIndex: 1 }}
            >
              Lottery Hub
              <ChevronDown
                size={16}
                strokeWidth={2.5}
                className={`dropdown-chevron ${
                  expandedMobileDropdown === "lotteries" ? "rotated" : ""
                }`}
                style={{ marginTop: "2px", transition: "transform 0.3s ease" }}
              />
            </Box>

            {/* Desktop dropdown */}
            <Box className="desktop-only">
              <CommonDropdown isVisible={isDropdownOpen} align="left">
                <CommonDropdownItem
                  icon={<Ticket size={20} />}
                  title={LOTTERY_NAMES.MEGABET}
                  subtitle="Main raffle lottery"
                  to={Path.alllottery}
                />
                <CommonDropdownItem
                  icon={<Dices size={20} />}
                  title={LOTTERY_NAMES.SPINBET}
                  subtitle="Spin and win"
                  to={Path.Spinnerlottery}
                />
                <CommonDropdownItem
                  icon={<Pickaxe size={20} />}
                  title={LOTTERY_NAMES.MINEBET}
                  subtitle="Mine for rewards"
                  to={Path.allminelottery}
                />
                <CommonDropdownItem
                  icon={<Rocket size={20} />}
                  title={LOTTERY_NAMES.ROCKETBET}
                  subtitle="Progressive rocket lottery"
                  to={Path.allmissilelottery}
                />
              </CommonDropdown>
            </Box>

            {/* Mobile submenu */}
            <Box
              className={`mobile-lottery-submenu mobile-only${
                expandedMobileDropdown === "lotteries"
                  ? " mobile-lottery-submenu--open"
                  : ""
              }`}
              style={{ position: "relative", zIndex: 1 }}
            >
              <Box className="mobile-lottery-inner">
                <Box
                  component={Link}
                  to={Path.alllottery}
                  className="mobile-lottery-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {LOTTERY_NAMES.MEGABET}
                </Box>
                <Box
                  component={Link}
                  to={Path.Spinnerlottery}
                  className="mobile-lottery-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {LOTTERY_NAMES.SPINBET}
                </Box>
                <Box
                  component={Link}
                  to={Path.allminelottery}
                  className="mobile-lottery-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {LOTTERY_NAMES.MINEBET}
                </Box>
                <Box
                  component={Link}
                  to={Path.allmissilelottery}
                  className="mobile-lottery-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {LOTTERY_NAMES.ROCKETBET}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Standard Navigation Links Iterated */}
          {[
            { name: "Gift Cards", path: Path.allgiftcards },
            { name: "Results", path: Path.result },
            { name: "Lottery Creator", path: Path.org },
            { name: "News", path: Path.news },
            { name: "Help", path: Path.help },
            { name: "About Us", path: Path.about },
          ].map((link) => (
            <Box
              component={Link}
              key={link.name}
              onClick={() => {
                if (window.innerWidth <= 1050) {
                  setIsMobileMenuOpen(false);
                }
              }}
              onMouseEnter={(e) => {
                handleNavHover(e);
                animateMobileNavBgIn(e);
              }}
              onTouchStart={animateMobileNavBgIn}
              onMouseLeave={animateMobileNavBgOut}
              onTouchEnd={animateMobileNavBgOut}
              className={`nav-link hovernav mobile-nav-item ${
                window.location.pathname.includes(link.path) ? "activenav" : ""
              }`}
              to={link.path}
              style={{ position: "relative", textDecoration: "none" }}
            >
              <Box
                className="mobile-hover-bg mobile-only"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "0%",
                  opacity: 0,
                  backgroundColor: colors.primary.light,
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
              <Box
                className="nav-link-content"
                style={{ position: "relative", zIndex: 1 }}
              >
                {link.name}
              </Box>
            </Box>
          ))}

          {/* Desktop Account Options (Sign in / Sign up OR Balance credits & Logout) */}
          <Box
            className="desktop-only"
            onMouseEnter={handleNavLeave}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <AccountNav />
          </Box>

          <Box
            onClick={() => {
              if (window.innerWidth <= 1050) {
                setIsMobileMenuOpen(false);
              }
            }}
          >
            {!walletAccount ? (
              <Box
                component="div"
                className="nav-link"
                onMouseEnter={handleNavLeave}
                style={{ padding: "0 10px" }}
              >
                <CommonButton
                  onClick={() => handleConnectWallet()}
                  variant="contained"
                  size="medium"
                  sx={{ borderRadius: "8px", px: 3, py: 1 }}
                >
                  Connect
                </CommonButton>
              </Box>
            ) : (
              <Box
                component={Link}
                to={Path.profile}
                className="nav-link accNAdd d-flex align-items-center hovernav"
                onMouseEnter={handleNavLeave}
                style={{ gap: "0.5rem", textDecoration: "none" }}
              >
                <Box
                  component="span"
                  className="AccountAddress"
                  style={{
                    color: "inherit",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                  }}
                >
                  {`${walletAccount.substring(0, 2)}.... ${walletAccount.substring(
                    walletAccount.length - 4,
                    walletAccount.length
                  )}`}
                </Box>
                <User
                  size={22}
                  color="currentColor"
                  style={{ cursor: "pointer" }}
                />
              </Box>
            )}
          </Box>

          {/* Mobile Account Section inside the mobile menu drawer */}
          <Box
            className="mobile-only mobile-nav-item mobile-account-row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <AccountNav onAction={() => setIsMobileMenuOpen(false)} />
          </Box>
        </Box>
      </Box>

      <Dialog
        classes={{ paper: "PopupStyle modal-content" }}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <Box className="modal-body">
          <Box className="container">
            <Box className="row">
              <Box className="col-lg-12 col-12">
                <Box className="message-text">{modalMessage}</Box>
                <Typography
                  component="p"
                  className="link-textt"
                  style={{ marginTop: "16px" }}
                >
                  <Box
                    component={Link}
                    to="/help"
                    style={{
                      color: colors.primary.main,
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    Get Help
                  </Box>
                </Typography>
              </Box>
              <Box className="col-12">
                <Box className="btn-div">
                  <MuiButton
                    variant="contained"
                    className="submit-btn btn btn-primary"
                    onClick={() => setIsModalOpen(false)}
                  >
                    OK
                  </MuiButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default NavBar;
